import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import PageLayout from "@/components/PageLayout";
import SEO from "@/components/SEO";
import FAQSchema from "@/components/FAQSchema";
import ContactModal from "@/components/new-landing/ContactModal";
import TrustBar from "@/components/TrustBar";
import { trackEvent } from "@/utils/analytics";

const NAVY = "#002178";

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6 },
};

const phases = [
  {
    number: "01",
    title: "Team Transformation",
    period: "Phase 1 (Apr-Jun)",
    description:
      "Assessed the entire product organization. Identified performers and rebuilt around them. Brought in senior talent, compressed timelines, and established new standards for execution.",
  },
  {
    number: "02",
    title: "Strategic Realignment",
    period: "Phase 2 (Jul-Sep)",
    description:
      "Killed every initiative that wasn't driving user value. Previous leadership chased conversion tweaks while the product experience failed users. We reoriented everything around one principle: solve for customer value, and conversions follow.",
  },
  {
    number: "03",
    title: "Execution Machine",
    period: "Phase 3 (Oct-Dec)",
    description:
      "Built a rigorous experimentation engine. 50+ experiments in a single quarter, each with clear success metrics, controlled rollouts, and documented results. One feature scoped as a multi-month engineering project was delivered in a single week, seeing immediate heavy usage.",
  },
];

const findings = [
  {
    stat: "<½",
    benchmark: "of industry benchmark",
    label: "Activation rate",
    description:
      "The product was actively pushing users away from value. The biggest acquisition channel couldn't even complete core tasks.",
  },
  {
    stat: "50%",
    benchmark: "first-year churn",
    label: "Customer retention",
    description:
      "Half of all new customers gone within 12 months. Acquiring customers at lower value than the ones leaving.",
  },
  {
    stat: "Zero",
    benchmark: "experiments",
    label: "Experimentation culture",
    description:
      "No structured experimentation. Decisions made on opinion, not data. Product managers couldn't answer basic questions about feature usage.",
  },
  {
    stat: "Millions",
    benchmark: "wasted annually",
    label: "Tooling and overhead",
    description:
      "Unused tools, redundant licenses, bloated processes. Nobody auditing spend against outcomes.",
  },
  {
    stat: "Lost",
    benchmark: "confidence",
    label: "Product leadership",
    description:
      "The CPO had lost the trust of the CEO, the board, and the team. Heavy investment, no financial return. The team knew it. The board knew it. Nobody acted.",
  },
  {
    stat: "Siloed",
    benchmark: "across functions",
    label: "Cross-functional collaboration",
    description:
      "Engineering, marketing, sales, and product operating independently. No shared goals, no shared metrics. Product couldn't explain spend. Marketing couldn't sell what product shipped.",
  },
  {
    stat: "Gridlock",
    benchmark: "by design",
    label: "Decision making",
    description:
      "Every decision required consensus from every stakeholder. No one made tough calls. The result: slow, safe choices that moved nothing. Deprioritized work continued anyway.",
  },
  {
    stat: "Net",
    benchmark: "negative",
    label: "Financial growth from product",
    description:
      "All revenue growth came from pricing and marketing spend. Product contributed nothing to financial targets in two years. The deal thesis depended on product-driven growth that wasn't happening.",
  },
];

const metrics = [
  {
    value: "2x",
    label: "Product activation rate doubled",
    detail: "From well below industry benchmark to above it",
  },
  {
    value: "Record",
    label: "All-time high conversion rate",
    detail: "Best performance in company history",
  },
  {
    value: "+50%",
    label: "Trial-to-paid conversion",
    detail: "Across all channels",
  },
  {
    value: "#1",
    label: "Best month in over a decade",
    detail: "Net new customer growth",
  },
  {
    value: "50+",
    label: "Experiments in one quarter",
    detail: "From near zero",
  },
  {
    value: "Millions",
    label: "Saved in tooling and team optimization",
    detail: "Leaner team, higher output",
  },
];

const testimonials = [
  {
    quote:
      "Love what you guys are doing since my restructure. Feedback is positive.",
    role: "CEO",
    group: "Leadership",
  },
  {
    quote:
      "For the first time in months, I felt like we meaningfully have a path to being net add positive.",
    role: "CEO",
    group: "Leadership",
  },
  {
    quote:
      "We were playing baseball and had no one who could catch. I feel like organizationally now we do.",
    role: "COO",
    group: "Leadership",
  },
  {
    quote:
      "You can't move quickly enough on poor performers and blockers in the organization. It is unbelievable how much they inhibit progress.",
    role: "COO",
    group: "Leadership",
  },
  {
    quote:
      "Loved your product growth presentation, learned a lot. Can you come and present to the leaders on my team so they are aware of the great work you're doing?",
    role: "CRO",
    group: "Cross-Functional",
  },
  {
    quote:
      "Previously we just built and hoped the metrics would change. You guys came in and reoriented everything around specific customer journey points.",
    role: "VP Engineering",
    group: "Cross-Functional",
  },
  {
    quote:
      "It's rare to work with product leaders who understand financial planning this deeply. Your modeling of product impact changed how we plan.",
    role: "Director of Finance",
    group: "Cross-Functional",
  },
  {
    quote:
      "Whether they're contractors or employees, they're going to be like employees. More than consultants to us.",
    role: "COO",
    group: "The Difference",
  },
];

const articleSchema = {
  "@context": "https://schema.org",
  "@type": "Article",
  headline: "Reversing Decade-Long Decline",
  description:
    "How FractionalCPO reversed decade-long decline at a PE-backed platform",
  author: { "@type": "Organization", name: "FractionalCPO" },
  publisher: { "@type": "Organization", name: "FractionalCPO" },
  "dateModified": "2026-03-29",
};

const caseStudyFaqs = [
  {
    question: "How long did this engagement take?",
    answer: "Nine months from start to record-breaking results. The first measurable improvements appeared within 60 days."
  },
  {
    question: "Can you do this for companies in other industries?",
    answer: "Yes. While this case study is in email marketing, our methodology applies to any B2B and B2C technology company. The pattern of identifying the 5% of work that drives impact is industry-agnostic."
  },
  {
    question: "What made this different from a consulting engagement?",
    answer: "We deliver financial results validated by executive leadership and the board. We restructured the team for performance, ran 50+ experiments in a single quarter, and delivered the best financial results in over a decade. Consultants deliver recommendations. FractionalCPO delivers outcomes."
  },
  {
    question: "How many people did you embed?",
    answer: "Six senior product leaders took interim roles across product strategy, growth, operations, and product marketing. A founding partner maintained direct oversight throughout the engagement."
  },
  {
    question: "What does the assessment phase look like?",
    answer: "We embed with your team for 2-4 weeks. We interview stakeholders, audit processes, analyze data, and assess team capabilities. You get a prioritized action plan showing exactly what to fix, why, and in what order."
  }
];

const CaseStudy = () => {
  const [isContactOpen, setIsContactOpen] = useState(false);

  return (
    <PageLayout>
      <SEO
        title="Case Study: Reversing Decade-Long Decline"
        description="How we reversed decade-long decline at a PE-backed platform. Doubled product conversion, measurable conversion improvements, record-breaking growth."
        path="/case-study"
        schema={articleSchema}
      />

      {/* Section 1: Hero */}
      <section className="py-16 md:py-24 px-6 md:px-12 lg:px-24">
        <div className="max-w-[1400px] mx-auto">
          <motion.p
            {...fadeUp}
            className="text-sm uppercase tracking-[0.3em] text-[#002178] mb-4 font-medium"
          >
            One example from a decade of embedded product leadership
          </motion.p>

          <motion.h1
            {...fadeUp}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="text-[clamp(3rem,8vw,5.5rem)] font-bold leading-[0.95] tracking-[-0.03em] mb-8"
          >
            Reversing{' '}
            <span className="text-[#002178]">Decade-Long Decline</span>
          </motion.h1>

          <motion.p
            {...fadeUp}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="text-[clamp(1.25rem,3vw,1.75rem)] font-light text-black/70 leading-[1.4] mb-8 max-w-2xl"
          >
            A PE-backed platform with hundreds of millions in revenue. Every growth metric declining for over a decade. Nine months later, every one reversed.
          </motion.p>

          <motion.div
            {...fadeUp}
            transition={{ duration: 0.6, delay: 0.25 }}
          >
            <button
              onClick={() => { trackEvent('contact_click', { source: 'hero' }); setIsContactOpen(true); }}
              className="px-8 py-4 bg-[#002178] text-white font-medium hover:scale-105 transition-transform mt-8"
            >
              Let's chat
            </button>
            <TrustBar />
          </motion.div>
        </div>
      </section>

      {/* Section 2: The Problem */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-[#F5F5F0]">
        <div className="max-w-[1400px] mx-auto">
          <div className="grid md:grid-cols-2 gap-16 md:gap-24 items-start">
            <div className="md:sticky md:top-32">
              <motion.p
                {...fadeUp}
                className="text-sm uppercase tracking-[0.3em] text-[#002178] mb-4 font-medium"
              >
                The Situation
              </motion.p>
              <motion.h2
                {...fadeUp}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="text-[clamp(2rem,5vw,3.5rem)] font-light leading-[1.1] tracking-[-0.02em] mb-8"
              >
                A growth engine{" "}
                <span className="text-[#002178] font-medium">in crisis</span>
              </motion.h2>
            </div>

            <div>
              <motion.div
                {...fadeUp}
                className="mb-12 py-8 border-b border-black/10"
              >
                <span className="text-[clamp(3rem,6vw,5rem)] font-light text-[#002178] leading-none block mb-2">
                  11 years
                </span>
                <p className="text-black/50 text-lg">
                  since their last record month
                </p>
              </motion.div>

              <motion.div
                {...fadeUp}
                transition={{ delay: 0.1 }}
                className="space-y-6"
              >
                <p className="text-black/60 leading-relaxed text-lg">
                  Revenue was growing on paper. But it was all pricing leverage and marketing spend. Product hadn't delivered meaningful value in two years. The company was acquiring customers at lower value than the ones it was losing.
                </p>
                <p className="text-black/60 leading-relaxed text-lg">
                  The product team was spending tens of millions a year building features nobody used. Their biggest bets had single-digit adoption. They'd built everything. They'd optimized nothing.
                </p>
                <p className="text-black/60 leading-relaxed text-lg">
                  The board had lost trust in product leadership to produce financial growth despite heavy investment. The CPO operated through consensus: every decision required buy-in from every stakeholder, creating gridlock. Nobody made tough calls. Everyone had to be happy with every decision. The result was slow, safe choices that moved nothing.
                </p>
                <p className="text-black/60 leading-relaxed text-lg">
                  Cross-functional trust had eroded. Engineering, marketing, and sales operated in silos. Product couldn't articulate what they were building or why. Marketing couldn't sell what product shipped. Finance couldn't connect product spend to business outcomes. The organization was stuck in a cycle of finger-pointing.
                </p>
                <p className="text-black/60 leading-relaxed text-lg">
                  Activation was broken. The product was at a fraction of the industry benchmark. Half of all new customers churned within their first year. There was no experimentation culture, no data discipline, and no accountability for outcomes.
                </p>
                <p className="font-medium text-black/70 leading-relaxed text-lg">
                  The deal thesis was at risk. They needed product leadership that could diagnose the real problem, make the hard calls nobody was making, and move fast enough to reverse it before the window closed.
                </p>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 2b: What We Found */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24">
        <div className="max-w-[1400px] mx-auto">
          <motion.p
            {...fadeUp}
            className="text-sm uppercase tracking-[0.3em] text-[#002178] mb-4 font-medium"
          >
            The Diagnosis
          </motion.p>
          <motion.h2
            {...fadeUp}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-[clamp(2rem,5vw,3.5rem)] font-light leading-[1.1] tracking-[-0.02em] mb-16"
          >
            What our assessment{" "}
            <span className="text-[#002178] font-medium">revealed</span>
          </motion.h2>

          <div className="grid md:grid-cols-2 gap-6">
            {findings.map((finding, i) => (
              <motion.div
                key={i}
                {...fadeUp}
                transition={{ delay: i * 0.1 }}
                className="border border-black/10 p-8 hover:border-[#002178]/25 transition-colors"
              >
                <div className="flex items-baseline gap-3 mb-3">
                  <span className="text-[clamp(2rem,4vw,3rem)] font-light text-[#002178] leading-none">
                    {finding.stat}
                  </span>
                  <span className="text-sm text-black/40">
                    {finding.benchmark}
                  </span>
                </div>
                <h3 className="text-lg font-medium mb-2">{finding.label}</h3>
                <p className="text-black/50 leading-relaxed">
                  {finding.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Section 3: What We Did */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24">
        <div className="max-w-[1400px] mx-auto">
          <motion.p
            {...fadeUp}
            className="text-sm uppercase tracking-[0.3em] text-[#002178] mb-4 font-medium"
          >
            The Approach
          </motion.p>
          <motion.h2
            {...fadeUp}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-[clamp(2rem,5vw,3.5rem)] font-light leading-[1.1] tracking-[-0.02em] mb-6"
          >
            What We Did
          </motion.h2>
          <motion.p
            {...fadeUp}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="text-lg text-black/60 leading-relaxed mb-16 max-w-2xl"
          >
            Embedded a team of our senior product leaders directly into operations. Not consulting from the sidelines, but taking interim leadership roles.
          </motion.p>

          <div className="relative">
            {/* Vertical timeline line */}
            <div className="hidden md:block absolute left-[27px] top-4 bottom-4 w-px bg-[#002178]/15" />

            <div className="space-y-0">
              {phases.map((phase, i) => (
                <motion.div
                  key={i}
                  {...fadeUp}
                  transition={{ delay: i * 0.15 }}
                  className="group relative md:pl-20 py-10 border-b border-black/10 last:border-b-0"
                >
                  {/* Timeline dot */}
                  <div className="hidden md:flex absolute left-0 top-10 w-[55px] h-[55px] rounded-full bg-[#002178] items-center justify-center">
                    <span className="text-white font-mono text-sm font-medium">
                      {phase.number}
                    </span>
                  </div>

                  <div>
                    <span className="text-sm text-[#002178]/60 font-medium uppercase tracking-wider">
                      {phase.period}
                    </span>
                    <h3 className="text-2xl md:text-3xl font-medium mt-2 mb-4 group-hover:text-[#002178] transition-colors flex items-baseline gap-3 md:block">
                      <span className="md:hidden font-mono text-[#002178]/30 text-lg flex-shrink-0">
                        {phase.number}
                      </span>
                      {phase.title}
                    </h3>
                    <p className="text-black/50 leading-relaxed text-lg max-w-2xl">
                      {phase.description}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Section 4: The Results */}
      <section
        className="py-24 md:py-32 px-6 md:px-12 lg:px-24"
        style={{ backgroundColor: NAVY }}
      >
        <div className="max-w-[1400px] mx-auto">
          <motion.p
            {...fadeUp}
            className="text-sm uppercase tracking-[0.3em] text-white/50 mb-4 font-medium"
          >
            The Outcome
          </motion.p>
          <motion.h2
            {...fadeUp}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-[clamp(2rem,5vw,3.5rem)] font-light leading-[1.1] tracking-[-0.02em] text-white mb-16"
          >
            The Results
          </motion.h2>

          <div className="grid grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
            {metrics.map((metric, i) => (
              <motion.div
                key={i}
                {...fadeUp}
                transition={{ delay: i * 0.1 }}
                className="border border-white/10 p-8 hover:border-white/25 transition-colors"
              >
                <span className="text-[clamp(2.5rem,5vw,4rem)] font-light text-white leading-none block mb-3">
                  {metric.value}
                </span>
                <p className="text-white/70 text-sm leading-relaxed font-medium mb-1">
                  {metric.label}
                </p>
                <p className="text-white/30 text-xs">{metric.detail}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Section 5: Testimonials */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-[#F5F5F0]">
        <div className="max-w-[1400px] mx-auto">
          <motion.h2
            {...fadeUp}
            className="text-[clamp(2rem,5vw,3.5rem)] font-light leading-[1.1] tracking-[-0.02em] mb-16"
          >
            In Their Words
          </motion.h2>

          <div className="grid md:grid-cols-2 gap-8">
            {testimonials.map((testimonial, i) => (
              <motion.blockquote
                key={i}
                {...fadeUp}
                transition={{ delay: i * 0.08 }}
                className="bg-white p-8 border border-black/5"
              >
                <p className="text-black/70 text-lg leading-relaxed italic mb-6">
                  &ldquo;{testimonial.quote}&rdquo;
                </p>
                <cite className="text-black/40 text-sm not-italic font-medium">
                  {testimonial.role}
                </cite>
              </motion.blockquote>
            ))}
          </div>
        </div>
      </section>

      {/* Section 5b: Why It Worked */}
      <section
        className="py-24 md:py-32 px-6 md:px-12 lg:px-24"
        style={{ backgroundColor: NAVY }}
      >
        <div className="max-w-[1400px] mx-auto">
          <div className="grid md:grid-cols-2 gap-16 md:gap-24">
            <div>
              <motion.p
                {...fadeUp}
                className="text-sm uppercase tracking-[0.3em] text-white/50 mb-4 font-medium"
              >
                The Difference
              </motion.p>
              <motion.h2
                {...fadeUp}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="text-[clamp(2rem,5vw,3.5rem)] font-light leading-[1.1] tracking-[-0.02em] text-white mb-8"
              >
                Why it <span className="font-medium">worked</span>
              </motion.h2>
              <motion.p
                {...fadeUp}
                transition={{ duration: 0.6, delay: 0.15 }}
                className="text-lg text-white/60 leading-relaxed"
              >
                This wasn't consulting. We didn't deliver a report and leave. We embedded inside the organization, took interim leadership roles, and were accountable for the numbers.
              </motion.p>
            </div>

            <div className="space-y-10">
              {[
                {
                  number: "01",
                  title: "Embedded, not advisory",
                  description:
                    "Six senior product leaders took roles inside the company. Daily standups, direct team management, hands on the product.",
                },
                {
                  number: "02",
                  title: "Hard decisions, fast",
                  description:
                    "Assessed the team in weeks, not quarters. Removed blockers. Brought in talent. Moved at startup velocity inside a thousand-person company.",
                },
                {
                  number: "03",
                  title: "Data over opinion",
                  description:
                    "Replaced gut-feel decision making with structured experimentation. Every change measured. Every hypothesis tested. Results shared transparently.",
                },
                {
                  number: "04",
                  title: "Accountable for outcomes",
                  description:
                    "Not accountable for hours logged or slides delivered. Accountable for activation rates, conversion, and net customer growth.",
                },
              ].map((item, i) => (
                <motion.div
                  key={i}
                  {...fadeUp}
                  transition={{ delay: i * 0.1 }}
                  className="flex gap-6 items-baseline"
                >
                  <span className="text-white/20 font-mono text-2xl flex-shrink-0">
                    {item.number}
                  </span>
                  <div>
                    <h3 className="text-white text-lg font-medium mb-2">
                      {item.title}
                    </h3>
                    <p className="text-white/50 leading-relaxed">
                      {item.description}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Section 6: FAQ */}
      <FAQSchema faqs={caseStudyFaqs} />

      {/* Section 7: CTA */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24">
        <div className="max-w-[1400px] mx-auto text-center">
          <motion.h2
            {...fadeUp}
            className="text-[clamp(2rem,5vw,3.5rem)] font-light leading-[1.1] tracking-[-0.02em] mb-6"
          >
            Want Similar Results?
          </motion.h2>
          <motion.p
            {...fadeUp}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-lg text-black/60 leading-relaxed mb-12 max-w-xl mx-auto"
          >
            Every engagement starts with understanding where you're stuck and
            what's possible. No slides. No 90-day discovery. Just a clear map
            of what to fix and why.
          </motion.p>
          <motion.div
            {...fadeUp}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <button
              onClick={() => { trackEvent('contact_click', { source: 'cta_block' }); setIsContactOpen(true); }}
              className="inline-block px-8 py-4 bg-[#002178] text-white font-medium hover:scale-105 transition-transform"
            >
              Let's chat
            </button>
          </motion.div>
        </div>
      </section>

      <ContactModal open={isContactOpen} onOpenChange={setIsContactOpen} />
    </PageLayout>
  );
};

export default CaseStudy;
